import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

const BLOCKED_KINDS = ['user', 'group'];

export const CatalogKindGuard = ({
  children,
}: {
  children: React.ReactElement;
}) => {
  const [searchParams] = useSearchParams();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean | null>(null);

  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);

  const kindFilter = searchParams.get('filters[kind]')?.toLowerCase();
  const isBlocked = !!kindFilter && BLOCKED_KINDS.includes(kindFilter);

  useEffect(() => {
    if (!isBlocked) {
      // No need to check permissions if there's nothing to block
      setIsPlatformAdmin(false);
      return;
    }

    let cancelled = false;

    const checkAdmin = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;
        const userName = userEntityRef.split('/')[1];

        let userEmail = userName;
        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const profile = userEntity?.spec?.profile as any;
          if (profile?.email) {
            userEmail = profile.email;
          }
        } catch {
          // fallback to username
        }

        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;

        const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${userEmail}`,
            action: 'platform_admin',
            resource: 'platform:main',
          }),
        });

        if (!cancelled) {
          if (response.ok) {
            const data = await response.json();
            setIsPlatformAdmin(data.allowed || false);
          } else {
            setIsPlatformAdmin(false);
          }
        }
      } catch {
        if (!cancelled) {
          setIsPlatformAdmin(false);
        }
      }
    };

    checkAdmin();
    return () => {
      cancelled = true;
    };
  }, [isBlocked, identityApi, catalogApi, config]);

  // Still loading the admin check
  if (isBlocked && isPlatformAdmin === null) {
    return null;
  }

  if (isBlocked && !isPlatformAdmin) {
    return <Navigate to="/catalog" replace />;
  }

  return children;
};
